import React, { FC, ReactNode } from 'react'

import cn from 'classnames'

import classes from './label.module.scss'

/**
 * Variant controls the visual style of the label text.
 * - `'form'`  – original DS form-field style (Kollektif/ExtraSmall, UI/LightGrey).
 * - `'one'`   – dashboard default: Montserrat/Large, Brand/PrimaryBlack.
 * - `'two'`   – dashboard inverted: Montserrat/Large, UI/White (e.g. on dark backgrounds).
 */
export type LabelVariant = 'form' | 'one' | 'two'

export interface LabelProps {
  text?: ReactNode
  htmlFor?: string
  variant?: LabelVariant
  children?: ReactNode
  showToggle?: ReactNode
  isInline?: boolean
  className?: string
  dataTestId?: string
  isLegacyStyle?: boolean
}

export const Label: FC<LabelProps> = ({
  text,
  htmlFor,
  children,
  isInline = false,
  variant,
  showToggle,
  className,
  dataTestId,
  isLegacyStyle = false,
}) => {
  const resolvedHtmlFor = htmlFor ?? ''

  const resolvedVariant: LabelVariant = (() => {
    return variant ? variant : 'form'
  })()

  const labelClassName = cn(className, {
    [classes.inlineLabel]: isInline,
    [classes.variantOne]: resolvedVariant === 'one',
    [classes.variantTwo]: resolvedVariant === 'two',
    [classes.legacyLabel]: isLegacyStyle,
  })

  const labelTextClassName = cn({
    [classes.labelText]: resolvedVariant === 'form',
  })

  const labelElement = (
    <label
      className={labelClassName}
      data-testid={
        dataTestId ||
        (resolvedHtmlFor ? resolvedHtmlFor + '-form-label' : undefined)
      }
      htmlFor={resolvedHtmlFor || undefined}
    >
      <span
        className={labelTextClassName}
        data-testid={
          resolvedHtmlFor ? resolvedHtmlFor + '-input-label' : undefined
        }
      >
        {text}
      </span>
      {children}
    </label>
  )

  if (!showToggle) return labelElement

  return (
    <>
      {labelElement}
      <div className={classes.toggleOverlay}>{showToggle}</div>
    </>
  )
}
